import {Brand} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/brand.types';
import {Price} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/price.types';
import {PriceV2} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/price_v2.types';
import {Product} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product.types';
import {
  BrandTracker,
  ProductTracker,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/tracker.types';
import {useTranslation} from '@aboutyou/i18n';
import {TrackingRecoElement} from '@aboutyou/legacy-tracking-sdk';
import {useTrackWishlist} from '@aboutyou/tracking';
import {WishButtonProps, useShowToast} from '@aboutyou/ui-components';
import {
  UseToggleProductInWishlistResults,
  useIsProductInWishlist,
  useToggleProductInWishlist,
} from '@aboutyou/user';
import React, {ComponentType, FC} from 'react';
import {useSubmitAddToWishlistMission} from '../../backend-services/Coins/utils/useSubmitAddToWishlistMission';
import {useUpdateShowNotification} from '../../backend-services/PushNotification/PushNotificationProvider';

interface WishlistButtonProps {
  product: Product | ProductTracker;
  price?: Price | PriceV2;
  brand: Brand | BrandTracker;
  WishlistComponent: ComponentType<WishButtonProps>;
  className?: string;
  style?: React.CSSProperties;
  testId?: string;
  streamPosition?: number;
  trackingData?: {
    sectionIndex?: number;
    sortingChannel?: string;
    pageType?: string;
    recoType?: string;
    elements?: TrackingRecoElement[];
  };
  withBackground?: boolean;
  onClickTrackingCallback?: (isInWishlist?: boolean) => void;
  isFocusable?: boolean;
  badgesListKeys: string[];
}

export const WishlistButton: FC<
  React.PropsWithChildren<WishlistButtonProps>
> = ({
  product,
  price,
  brand,
  className,
  WishlistComponent,
  style,
  children,
  testId = 'addToWishlist',
  streamPosition = NaN,
  trackingData,
  withBackground,
  isFocusable,
  badgesListKeys,
}) => {
  const tracker = useTrackWishlist({
    streamPosition,
    product,
    price,
    brand,
    trackingData,
    badges: badgesListKeys,
  });
  const {t} = useTranslation();
  const showToast = useShowToast();
  const productId = 'productId' in product ? product.productId : product.id;
  const isInWishlist = useIsProductInWishlist(productId);
  const updatePushNotification = useUpdateShowNotification();
  const onAddToWishlistSuccess = useSubmitAddToWishlistMission();

  const showErrorToast = (err?: string) => {
    showToast({
      content: err || t('Oops! Something went wrong!'),
      type: 'ERROR',
      'data-testid': 'addToWishlistButtonToast',
    });
  };

  const toggleProductInWishlist = useToggleProductInWishlist({
    tracker,
    onError: showErrorToast,
  });

  const handleWishlistClick: React.MouseEventHandler = async event => {
    event.stopPropagation();
    event.preventDefault();
    const result = await toggleProductInWishlist({
      productId,
      onAddSuccess: onAddToWishlistSuccess,
    });
    if (
      result &&
      result === UseToggleProductInWishlistResults.ADDED &&
      updatePushNotification
    ) {
      updatePushNotification(price);
    }
  };

  const addAriaLabel = typeof children !== 'string';
  const ariaLabel = isInWishlist
    ? t('Remove from the wishlist')
    : t('Add to the wishlist');

  return (
    <WishlistComponent
      className={className}
      data-testid={testId}
      onClick={handleWishlistClick}
      variant={isInWishlist ? 'filled' : 'not-filled'}
      style={style}
      withBackground={withBackground}
      aria-label={addAriaLabel ? ariaLabel : undefined}
      isFocusable={isFocusable}
    >
      {children}
    </WishlistComponent>
  );
};
